import { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { RELEASE_NOTES } from "@/releaseNotes";
import api from "@/lib/api";
import { skipNextUnloadPrompt } from "@/lib/leaveRegistry";

const POLL_MS = 5 * 60 * 1000;
const SNOOZE_MS = 60 * 60 * 1000;
/** Saisie en cours ? — les formulaires exposent window.__deskDirty (fonction ou booléen). */
export const isDirty = () => { const d = window.__deskDirty; return typeof d === "function" ? !!d() : !!d; };
export const setDeskDirty = (v) => { window.__deskDirty = v; };

/** Toast non bloquant « Mise à jour disponible » — compare /api/version au build chargé ; un seul toast, rappel horaire, application auto au changement de module ou au retour sur l'onglet si aucune saisie. */
export default function UpdateNotifier() {
  const [pending, setPending] = useState(false);
  const [visible, setVisible] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  const initial = useRef(null);
  const snoozedAt = useRef(0);

  const check = async () => {
    try {
      const { data } = await api.get("/version");
      if (initial.current === null) { initial.current = data.version; return; }
      if (data.version !== initial.current) { const at = Number(data.version) ? new Date(Number(data.version) * 1000) : null; setPending(true); setPublishedAt(at); window.__deskUpdate = { pending: true, publishedAt: at }; }
    } catch { /* hors ligne */ }
  };
  useEffect(() => { check(); const t = setInterval(check, POLL_MS); const onVis = () => document.visibilityState === "visible" && check(); document.addEventListener("visibilitychange", onVis); return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); }; }, []);
  useEffect(() => { if (pending && Date.now() - snoozedAt.current > SNOOZE_MS) setVisible(true); }, [pending]);
  useEffect(() => { if (!pending || visible) return undefined; const t = setInterval(() => { if (Date.now() - snoozedAt.current > SNOOZE_MS) setVisible(true); }, 60 * 1000); return () => clearInterval(t); }, [pending, visible]);

  // iter358 (point 2) — AUCUN rechargement automatique : l'utilisateur décide ; une saisie en cours n'est jamais perdue.
  const reload = () => { if (isDirty()) { setBlocked(true); return; } skipNextUnloadPrompt(); window.location.reload(); };
  const later = () => { snoozedAt.current = Date.now(); setVisible(false); setBlocked(false); };
  if (!visible) return null;
  const notes = (RELEASE_NOTES || []).filter(Boolean).slice(0, 3);
  const hhmm = publishedAt ? publishedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="fixed" style={{ right: 18, bottom: 18, zIndex: 200, width: 330, background: "#1c1c22", border: "0.5px solid #34343c", borderRadius: 10, padding: "12px 14px", boxShadow: "0 12px 32px rgba(0,0,0,.45)", fontFamily: "Tahoma, sans-serif" }} data-testid="update-card">
      <div className="flex items-center" style={{ gap: 8 }}>
        <Sparkles size={15} style={{ color: "#b3a6ff" }} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "#f2f2f5", flex: 1 }}>Mise à jour disponible</span>
        {hhmm && <span className="tabular-nums" style={{ fontSize: 10.5, color: "#8a8a93" }} data-testid="update-card-time">{hhmm}</span>}
      </div>
      {notes.length > 0 && <ul style={{ margin: "9px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 4 }} data-testid="update-card-notes">
        {notes.map((n, i) => <li key={i} className="flex" style={{ gap: 7, fontSize: 11, color: "#cfcdc6", lineHeight: "15px" }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#7a6cf0", marginTop: 5, flexShrink: 0 }} />{n}</li>)}
      </ul>}
      {blocked && <div style={{ fontSize: 10.5, color: "#E0A42E", marginTop: 8 }} data-testid="update-card-blocked">Enregistrez d'abord votre saisie.</div>}
      <div className="flex justify-end" style={{ gap: 8, marginTop: 10 }}>
        <button type="button" onClick={later} style={{ fontSize: 11.5, padding: "6px 12px", borderRadius: 7, border: "1px solid #34343c", color: "#c9c9d1" }} data-testid="update-card-later">Plus tard</button>
        <button type="button" onClick={reload} style={{ fontSize: 11.5, padding: "6px 14px", borderRadius: 7, background: "#7a6cf0", color: "#fff", fontWeight: 600 }} data-testid="update-card-reload">Recharger</button>
      </div>
    </div>
  );
}
